import { Grid, H2, H3 } from '@daren/ui-components'
import formatDate from 'date-fns/format'
import * as React from 'react'
import { useLoaderData } from 'remix'

import type { LoaderData } from './blog-page.server'

import { BackLink, Link } from '~/components/buttons'
import { PortableText, FourOhFour } from '~/components/common'
import { Container, Section } from '~/components/layout'
import { Preview } from '~/components/utils/preview'
import { useTranslations } from '~/context/translations-provider'
import { getBlogSlug } from '~/lib/routes'
import { PageType } from '~/types'
import { localizeSlug } from '~/utils/i18n'
import { blogMeta } from '~/utils/seo'

export const meta = blogMeta

export function CatchBoundary() {
  return <FourOhFour />
}

export default function BlogPage() {
  const { currentLanguage, t } = useTranslations()

  const {
    data: initialData,
    lastPosts,
    preview,
    query,
    params,
  } = useLoaderData<LoaderData>()
  const [data, setData] = React.useState(initialData)

  // on page change we need to update the data
  React.useEffect(() => {
    setData(initialData)
  }, [initialData])

  const { body, title, publishedAt, slug } = data || {}

  return (
    <>
      {preview && (
        <Preview data={data} setData={setData} query={query} params={params} />
      )}
      <main>
        <Section>
          <Grid className="mt-24 mb-14 lg:mb-24">
            <Container size="full">
              <article className="relative mx-auto max-w-3xl xl:grid xl:grid-cols-[1fr_50rem] xl:gap-x-8 xl:max-w-none">
                <div className="col-span-full mx-auto mb-16 max-w-7xl">
                  <div className="text-center text-slate-700 dark:text-slate-400 sm:text-center">
                    <time dateTime={publishedAt}>
                      {formatDate(new Date(publishedAt), 'dd MMMM yyyy')}
                    </time>
                    <H2 className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                      {title}
                    </H2>
                  </div>
                </div>
                <div className="mb-16 text-sm leading-6 xl:mb-0">
                  <BackLink href={`/${currentLanguage}/nieuws/`}>
                    Alle nieuwsberichten
                  </BackLink>
                </div>
                <div className="space-y-16">
                  {body && (
                    <div className="prose prose-slate dark:prose-dark">
                      <PortableText blocks={body} />
                    </div>
                  )}
                  {lastPosts && (
                    <div>
                      <H3 className="mb-6">{t('blog-latest-posts')}</H3>
                      <ul className="flex flex-col flex-wrap justify-between mb-12">
                        {lastPosts
                          .filter(post => post.slug !== slug)
                          .map(post => (
                            <li key={post.title}>
                              <Link
                                href={localizeSlug(
                                  getBlogSlug(
                                    post.publishedAt,
                                    post.slug ?? '',
                                  ),
                                  post?.lang,
                                  PageType.blog,
                                )}
                              >
                                {post.title}
                              </Link>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            </Container>
          </Grid>
        </Section>
      </main>
    </>
  )
}
